import { Auth } from '@aws-amplify/auth';
import * as Sentry from '@sentry/react';
import { router } from '@seller-hermes/app/globals';
import { resetDashboardContextBootstrap } from '@seller-hermes/features/portal/context-bootstrap';
import { getActiveSellerGroupId } from '@seller-hermes/features/portal/portal-local-context';
import { hasCachedCognitoSession } from '@seller-hermes/utils/cognito-session-cache';
import getCognitoError from '@seller-hermes/utils/getCognitoError';
import { withTimeout } from '@seller-hermes/utils/promise-timeout';
import {
  isLocalDashboardRuntime,
  isStandaloneLoginRuntime,
} from '@seller-hermes/utils/runtime';
import { publishLogoutMarker } from '@seller-hermes/utils/auth-session-expiry';
import { leavePageTo } from '@seller-hermes/utils/chunk-error';
import { action, Action, Computed, computed, thunk, Thunk } from 'easy-peasy';
import { toast } from 'react-toastify';
import { Injections, IStoreModel } from '../root.store';
import sellerGroupUserStoreModel, {
  SellerGroupUserStoreModel,
} from './seller-group-user.store';

interface CognitoUser {
  attributes: {
    sub: string;
    'custom:seller_id': string;
    email: string;
  };
}

const ALLOWED_ORIGINS =
  /^https:\/\/(([\w-]+\.)*abbiamolog\.com|(([\w-]+--)?abbiamo-insights\.netlify\.app))$/;
const ALLOWED_ORIGINS_DEV = /^https?:\/\/localhost(:\d+)?$/;
const AUTH_CHECK_TIMEOUT_MS = 5000;

export const isValidRedirectUri = (uri: string): boolean => {
  try {
    const { origin } = new URL(uri);
    if (ALLOWED_ORIGINS.test(origin)) return true;
    if (isLocalDashboardRuntime() && ALLOWED_ORIGINS_DEV.test(origin))
      return true;
    return false;
  } catch {
    return false;
  }
};

export interface LoginStoreModel {
  isStarted: boolean;
  setIsStarted: Action<LoginStoreModel, boolean>;
  sellerGroupUser: SellerGroupUserStoreModel;
  start: Thunk<LoginStoreModel, undefined, Injections, IStoreModel>;
  isLoading: boolean;
  setIsLoading: Action<LoginStoreModel, LoginStoreModel['isLoading']>;
  isLogged: boolean;
  setIsLogged: Action<LoginStoreModel, LoginStoreModel['isLogged']>;
  sellerId: Computed<LoginStoreModel, string | null>;
  email: Computed<LoginStoreModel, string | null>;
  login: Thunk<
    LoginStoreModel,
    { email: string; password: string; redirectUri?: string },
    Injections
  >;
  logout: Thunk<LoginStoreModel, undefined, Injections>;
  confirmPassword: Thunk<
    LoginStoreModel,
    { password: string; confimPassword: string },
    Injections
  >;
  // MFA (Cognito SOFTWARE_TOKEN_MFA): desafio TOTP no login standalone do Hermes.
  // Só dispara pra quem tem MFA preferido no Cognito. O enrollment forçado
  // (pra usuários com users.mfa_required) é feito no PORTAL, não aqui.
  // Ver docs/pay-into-hermes.md §4.3 + Obsidian "Pay no Hermes — MFA".
  confirmMfa: Thunk<LoginStoreModel, { code: string }, Injections>;
  pendingRedirectUri?: string;
  setPendingRedirectUri: Action<LoginStoreModel, string | undefined>;
  user: null | CognitoUser;
  setUser: Action<LoginStoreModel, LoginStoreModel['user']>;
  cachedUser: null | any;
  setCachedUser: Action<LoginStoreModel, LoginStoreModel['cachedUser']>;
  sendResetPasswordEmail: Thunk<LoginStoreModel, string>;
  resetPassword: Thunk<
    LoginStoreModel,
    { code: string; email: string; password: string },
    Injections
  >;
  auth: Thunk<LoginStoreModel, LoginStoreModel['user'], Injections>;
}

// `isStarted` NÃO é estado só de memória: o `persist(storeModel)` da raiz
// (root.store.ts) grava o slice `login` INTEIRO no sessionStorage — inclusive
// `sellerGroupUser.user`, porque o `allow` do persist aninhado limita só o
// persist dele, não o de cima. Numa aba reidratada o atalho de `start()` abaixo
// via `isStarted: true` + `isLogged: true` e saía sem refazer o /me: o
// `state.user` ficava congelado no snapshot da carga anterior, e mudança de
// `feature_config` (ex.: ligar `orders-page-v3` num seller group), de permissão
// ou de filial só aparecia em aba nova. Quem lê a flag lê só esse `state.user`
// (hooks/useSellerGroupFeatureFlag.ts), então a tela de Pedidos continuava na
// v1 com a flag já ligada no banco.
//
// Este marcador é de módulo, então zera a cada carga de página: o primeiro
// `start()` da carga sempre refaz o /me, e as chamadas seguintes (routes.tsx
// chama `start()` em 4 loaders) seguem usando o atalho.
let hasFetchedMeInThisPageLoad = false;

// Só para teste: o marcador é de módulo e o jest compartilha o módulo entre os
// casos do mesmo arquivo.
export const resetPageLoadMeMarkerForTests = () => {
  hasFetchedMeInThisPageLoad = false;
};

const loginStoreModel: LoginStoreModel = {
  isStarted: false,
  setIsStarted: action((state, payload) => {
    state.isStarted = payload;
  }),
  sellerGroupUser: sellerGroupUserStoreModel,
  auth: thunk(async (actions, user) => {
    actions.setUser(user);
    actions.setIsLogged(true);
    await actions.sellerGroupUser.start();
    hasFetchedMeInThisPageLoad = true;
  }),
  email: computed((s) =>
    s.user?.attributes ? s.user.attributes['email'] : null,
  ),
  sellerId: computed((s) =>
    s.user?.attributes ? s.user.attributes['custom:seller_id'] : null,
  ),
  start: thunk(async (actions, _, { injections, getState }) => {
    if (getState().isStarted && hasFetchedMeInThisPageLoad) {
      // Se o seller group ativo (cookie compartilhado entre LOG/GO/PAY,
      // revalidado por bootstrapDashboardContext antes deste start() rodar —
      // ver routes.tsx) não bate com o seller group do state.user já
      // persistido, o state.user está desatualizado (troca de ambiente ou de
      // produto, com ou sem troca de origin) e precisa ser refeito mesmo com
      // isStarted=true. Sem `user` no state não há o que reaproveitar: também
      // refaz.
      const activeSellerGroupId = getActiveSellerGroupId();
      const cachedSellerGroupId =
        getState().sellerGroupUser.user?.seller_group_id;
      const userMatchesActiveContext =
        cachedSellerGroupId != null &&
        (activeSellerGroupId == null ||
          activeSellerGroupId === cachedSellerGroupId);

      // Se já foi iniciado mas o usuário não está logado e há uma sessão
      // Cognito nos cookies (ex: handoff do portal via cookie storage),
      // re-autentica em vez de sair cedo. Isso corrige o caso em que
      // isStarted: true fica persistido no localStorage após um logout,
      // mas uma nova sessão válida está disponível nos cookies.
      if (
        userMatchesActiveContext &&
        (getState().isLogged || !hasCachedCognitoSession())
      ) {
        actions.setIsLoading(false);
        return;
      }
    }
    actions.setIsLoading(true);
    if (!hasCachedCognitoSession()) {
      actions.setIsLoading(false);
      actions.setIsStarted(true);
      return;
    }

    const user = await withTimeout(
      Auth.currentAuthenticatedUser(),
      AUTH_CHECK_TIMEOUT_MS,
      'Cognito user check timed out',
    ).catch(() => null);
    try {
      if (user) {
        await actions.auth(user);
      }
    } catch (err) {
      actions.logout();
      toast.error(
        injections.intl.t(`no-seller-related`, {
          ns: 'errors',
        }) as string,
      );
    }
    actions.setIsLoading(false);
    actions.setIsStarted(true);
  }),
  isLoading: true,
  setIsLoading: action((state, payload) => {
    state.isLoading = payload;
  }),
  isLogged: false,
  setIsLogged: action((state, payload) => {
    state.isLogged = payload;
  }),
  user: null,
  setUser: action((state, payload) => {
    state.user = payload;
  }),
  cachedUser: null,
  setCachedUser: action((state, payload) => {
    state.cachedUser = payload;
  }),
  login: thunk(async (actions, payload, { injections }) => {
    try {
      const user = await Auth.signIn({
        password: payload.password,
        username: payload.email,
      });
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        actions.setCachedUser(user);
        router.navigate('/login/new-password');
        return;
      }
      // MFA (Cognito): usuário com TOTP preferido cai aqui. Guarda o user e o
      // redirectUri e manda pra tela de OTP; a continuação roda no confirmMfa.
      if (
        user.challengeName === 'SOFTWARE_TOKEN_MFA' ||
        user.challengeName === 'SMS_MFA'
      ) {
        actions.setCachedUser(user);
        actions.setPendingRedirectUri(payload.redirectUri);
        router.navigate('/login/mfa');
        return;
      }
      if (isStandaloneLoginRuntime() && !payload.redirectUri) {
        actions.setUser(user);
        actions.setIsLogged(true);
        actions.setIsLoading(false);
        actions.setIsStarted(false);
        router.navigate('/login/context');
        return;
      }

      await actions.auth(user);
      if (payload.redirectUri && isValidRedirectUri(payload.redirectUri)) {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        const refreshToken = session.getRefreshToken().getToken();
        leavePageTo(`${payload.redirectUri}#id_token=${idToken}&refresh_token=${refreshToken}`);
        return;
      }
      router.navigate('/');
    } catch (err) {
      const cognitoError = getCognitoError(err);

      toast.error(injections.intl.t(`cognito-error:${cognitoError}`) as string);
    }
  }),
  logout: thunk(async (actions, _) => {
    publishLogoutMarker();
    await Auth.signOut();
    actions.setIsLogged(false);
    actions.setUser(null);
    actions.sellerGroupUser.reset();
    resetDashboardContextBootstrap();
    Sentry.setUser(null);
    leavePageTo(
      isStandaloneLoginRuntime() ? '/login' : (process.env['NX_PORTAL_URL'] ?? '/'),
    );
  }),
  confirmPassword: thunk(async (actions, payload, { injections, getState }) => {
    const user = getState().cachedUser;
    if (!user) {
      router.navigate('/login');
      return;
    }
    try {
      await Auth.completeNewPassword(user, payload.password);
      await actions.login({ email: user.username, password: payload.password });
    } catch (err) {
      const cognitoError = getCognitoError(err);
      toast.error(injections.intl.t(`cognito-error:${cognitoError}`) as string);
    }
  }),
  pendingRedirectUri: undefined,
  setPendingRedirectUri: action((state, payload) => {
    state.pendingRedirectUri = payload;
  }),
  confirmMfa: thunk(async (actions, payload, { injections, getState }) => {
    const cached = getState().cachedUser;
    if (!cached) {
      router.navigate('/login');
      return;
    }
    try {
      const user = await Auth.confirmSignIn(
        cached,
        payload.code,
        'SOFTWARE_TOKEN_MFA',
      );
      const redirectUri = getState().pendingRedirectUri;
      actions.setPendingRedirectUri(undefined);
      actions.setCachedUser(null);

      // Mesma continuação do `login` após o signIn.
      if (isStandaloneLoginRuntime() && !redirectUri) {
        actions.setUser(user);
        actions.setIsLogged(true);
        actions.setIsLoading(false);
        actions.setIsStarted(false);
        router.navigate('/login/context');
        return;
      }
      await actions.auth(user);
      if (redirectUri && isValidRedirectUri(redirectUri)) {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        const refreshToken = session.getRefreshToken().getToken();
        leavePageTo(`${redirectUri}#id_token=${idToken}&refresh_token=${refreshToken}`);
        return;
      }
      router.navigate('/');
    } catch (err) {
      const cognitoError = getCognitoError(err);
      toast.error(injections.intl.t(`cognito-error:${cognitoError}`) as string);
    }
  }),
  sendResetPasswordEmail: thunk(async (_, email) => {
    await Auth.forgotPassword(email);
  }),
  resetPassword: thunk(async (actions, payload, { injections }) => {
    const { email, password, code } = payload;
    try {
      await Auth.forgotPasswordSubmit(email, code, password);
      await actions.login({ email, password });
    } catch (err) {
      const cognitoError = getCognitoError(err);
      toast.error(injections.intl.t(`cognito-error:${cognitoError}`) as string);
    }
  }),
};

export default loginStoreModel;
